import { create } from 'zustand'
import type { Drone, Violation, ApiStatus } from './types'

interface DashboardState {
  drones: Drone[]
  violations: Violation[]
  apiStatus: ApiStatus
  lastUpdate: string
  droneStatus: string
  violationStatus: string
  selectedViolation: Violation | null
  showViolationsPanel: boolean
  violationInLastHour: number
  dronesInViolation: number
}

interface DashboardActions {
  setDrones: (drones: Drone[]) => void
  setViolations: (violations: Violation[]) => void
  setApiStatus: (status: ApiStatus) => void
  setLastUpdate: () => void
  setDroneStatus: (status: string) => void
  setViolationStatus: (status: string) => void
  setSelectedViolation: (violation: Violation | null) => void
  toggleViolationsPanel: () => void
  reset: () => void
  computeStats: () => void
}

export type DashboardStore = DashboardState & DashboardActions

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  // State
  drones: [],
  violations: [],
  apiStatus: 'unknown',
  lastUpdate: '-',
  droneStatus: 'Initializing...',
  violationStatus: 'Initializing...',
  selectedViolation: null,
  showViolationsPanel: true,
  violationInLastHour: 0,
  dronesInViolation: 0,

  // Actions
  setDrones: (drones) => {
    set({ drones })
    get().computeStats()
  },
  setViolations: (violations) => {
    set({ violations })
    get().computeStats()
  },
  setApiStatus: (apiStatus) => set({ apiStatus }),
  setLastUpdate: () => set({ lastUpdate: new Date().toLocaleTimeString() }),
  setDroneStatus: (droneStatus) => set({ droneStatus }),
  setViolationStatus: (violationStatus) => set({ violationStatus }),
  setSelectedViolation: (selectedViolation) => set({ selectedViolation }),
  toggleViolationsPanel: () => set((state) => ({ showViolationsPanel: !state.showViolationsPanel })),
  reset: () =>
    set({
      drones: [],
      violations: [],
      apiStatus: 'unknown',
      lastUpdate: '-',
      droneStatus: 'Initializing...',
      violationStatus: 'Initializing...',
      selectedViolation: null,
      violationInLastHour: 0,
      dronesInViolation: 0,
    }),
  computeStats: () => {
    const { drones, violations } = get()
    const now = Date.now()
    const violationInLastHour = violations.filter(
      (v) => now - new Date(v.timestamp).getTime() < 3600000,
    ).length
    const dronesInViolation = drones.filter((d) => {
      const distance = Math.sqrt(d.x ** 2 + d.y ** 2)
      return distance <= 1000
    }).length
    set({ violationInLastHour, dronesInViolation })
  },
}))
